import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API from "../config/API";

function UserPaymentDetails() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);

  const [toast, setToast] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(null);
  const [payingUser, setPayingUser] = useState(null);

  // =========================================================
  // AUTH HEADERS
  // =========================================================

  const getAuthHeaders = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));

      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.jwtToken}`,
      };
    } catch (error) {
      console.error("Unable to read user:", error);

      return {
        "Content-Type": "application/json",
      };
    }
  };

  // =========================================================
  // TOAST
  // =========================================================

  const showToast = (type, message) => {
    setToast({
      type,
      message,
    });

    setTimeout(() => {
      setToast(null);
    }, 2500);
  };

  // =========================================================
  // FETCH USERS
  // =========================================================

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const user = JSON.parse(localStorage.getItem("user"));

      if (!user?.jwtToken) {
        showToast("error", "Session expired. Please login again.");

        localStorage.clear();
        sessionStorage.clear();

        setTimeout(() => {
          navigate("/");
        }, 1500);

        return;
      }

      const emailId = user?.emailId;

      if (!emailId) {
        showToast("error", "Email ID not found.");
        return;
      }

      const url =
        `${API.V1}/user/getAllUserData` +
        `?emailId=${encodeURIComponent(emailId)}`;

      console.log("Fetching URL:", url);

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.jwtToken}`,
        },
      });

      console.log("Tenants/Guests Response:", response.data);

      if (response.data?.statusCode === 200) {
        setUsers(response.data?.data || []);
      } else {
        showToast("error", response.data?.message || "No data found");
        setUsers([]);
      }
    } catch (error) {
      console.error("Fetch tenants/guests error:", error);

      if (error.response?.status === 401 || error.response?.status === 403) {
        showToast("error", "Session expired. Please login again.");

        localStorage.clear();
        sessionStorage.clear();

        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        showToast(
          "error",
          error.response?.data?.message || "Unable to load tenants and guests",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // PAYMENT STATUS
  // =========================================================

  const getPaymentStatus = (user) => {
    return String(user?.paymentStatus || "")
      .trim()
      .toUpperCase();
  };

  // =========================================================
  // CALCULATE PENDING AMOUNT
  // =========================================================

  const getPendingAmount = (user) => {
    const bedCost = Number(user?.userBedCost || 0);
    const paidAmount = Number(user?.userPaidAmount || 0);

    const pending = bedCost - paidAmount;

    return pending > 0 ? pending : 0;
  };

  // =========================================================
  // CHECK PENDING PAYMENT
  // =========================================================

  const isPendingPayment = (user) => {
    const pendingAmount = getPendingAmount(user);
    const paymentStatus = getPaymentStatus(user);

    return (
      pendingAmount > 0 ||
      paymentStatus === "PENDING" ||
      paymentStatus === "PARTIALLY_PAID"
    );
  };

  // =========================================================
  // CHECK PAID PAYMENT
  // =========================================================

  const isPaidPayment = (user) => {
    const pendingAmount = getPendingAmount(user);
    const paymentStatus = getPaymentStatus(user);

    return (
      pendingAmount <= 0 &&
      (paymentStatus === "PAID" || paymentStatus === "SUCCESS")
    );
  };

  // =========================================================
  // FILTER USERS
  // =========================================================

  const filteredUsers = useMemo(() => {
    if (filter === "PENDING") {
      return users.filter((user) => isPendingPayment(user));
    }

    if (filter === "PAID") {
      return users.filter((user) => isPaidPayment(user));
    }

    return users;
  }, [users, filter]);

  // =========================================================
  // FORMAT DATE
  // =========================================================

  const formatBedDate = (date) => {
    if (!date) {
      return "-";
    }

    try {
      const parsedDate = new Date(date);

      if (isNaN(parsedDate.getTime())) {
        return date;
      }

      return parsedDate.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return "-";
    }
  };

  // =========================================================
  // PAYMENT TYPE
  // =========================================================

  const getPaymentType = (user) => {
    const paymentType = String(user?.paymentType || "")
      .trim()
      .toUpperCase();

    return paymentType || "-";
  };

  // =========================================================
  // PAY AMOUNT
  // =========================================================

  const handlePayAmount = (user) => {
    const pendingAmount = getPendingAmount(user);

    if (pendingAmount <= 0) {
      showToast("error", "No pending amount available.");
      return;
    }

    setPayingUser(user);

    navigate("/userPayPendingAmount", {
      state: {
        userRegisterId: user.userRegisterId,
        userBookingId: user.userBookingId,

        fullName: user.fullName,
        emailId: user.emailId,
        contactNo: user.contactNo,

        bedId: user.userBedId,
        bedNumber: user.userBedName,
        bedStatus: user.bedStatus,

        userBedCost: Number(user.userBedCost || 0),

        userPaidAmount: Number(user.userPaidAmount || 0),

        pendingAmount: pendingAmount,

        paymentType: user.paymentType,
        paymentStatus: user.paymentStatus,

        userBedJoiningDate: user.userBedJoiningDate,

        userBedVacateDate: user.userBedVacateDate,
      },
    });
  };

  // =========================================================
  // SEND MESSAGE
  // =========================================================

  const handleSendMessage = async (user) => {
    try {
      setSendingMessage(user.userRegisterId);

      const pendingAmount = getPendingAmount(user);

      const payload = {
        userRegisterId: user.userRegisterId,

        fullName: user.fullName,

        emailId: user.emailId,

        contactNo: user.contactNo,

        pendingAmount: pendingAmount,

        bedNumber: user.userBedName || "-",
      };

      console.log("Send Message Payload:", payload);

      const response = await axios.post(
        `${API.V1}/user/sendPaymentPendingMessage`,
        payload,
        {
          headers: getAuthHeaders(),
        },
      );

      if (response.data?.statusCode === 200) {
        showToast("success", "Payment message sent successfully.");
      } else {
        showToast("error", response.data?.message || "Unable to send message.");
      }
    } catch (error) {
      console.error("Send message error:", error);

      if (error.response?.status === 401 || error.response?.status === 403) {
        showToast("error", "Session expired. Please login again.");

        localStorage.clear();
        sessionStorage.clear();

        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        showToast(
          "error",
          error.response?.data?.message || "Unable to send message.",
        );
      }
    } finally {
      setSendingMessage(null);
    }
  };

  // =========================================================
  // COUNTS
  // =========================================================

  const pendingCount = users.filter((user) => isPendingPayment(user)).length;

  const paidCount = users.filter((user) => isPaidPayment(user)).length;

  // =========================================================
  // PAYMENT TOTALS
  // =========================================================

  const totalBedsAmount = users.reduce(
    (total, user) => total + Number(user?.userBedCost || 0),
    0,
  );

  const totalPaidAmount = users.reduce(
    (total, user) => total + Number(user?.userPaidAmount || 0),
    0,
  );

  const totalPendingAmount = users.reduce(
    (total, user) => total + getPendingAmount(user),
    0,
  );

  // =========================================================
  // PAYMENT STATUS UI
  // =========================================================

  const getPaymentStatusStyle = (user) => {
    const status = getPaymentStatus(user);

    if (isPaidPayment(user)) {
      return {
        background: "#dcfce7",
        color: "#166534",
      };
    }

    if (status === "PARTIALLY_PAID") {
      return {
        background: "#fef3c7",
        color: "#92400e",
      };
    }

    return {
      background: "#fee2e2",
      color: "#b91c1c",
    };
  };

  const getPaymentStatusLabel = (user) => {
    const status = getPaymentStatus(user);

    if (isPaidPayment(user)) {
      return "PAID";
    }

    if (status === "PARTIALLY_PAID") {
      return "PARTIALLY PAID";
    }

    if (status === "PENDING") {
      return "PENDING";
    }

    if (getPendingAmount(user) > 0) {
      return "PENDING";
    }

    return status || "-";
  };

  // =========================================================
  // BED STATUS
  // =========================================================

  const getBedStatusStyle = (user) => {
    const status = String(user?.bedStatus || "").toUpperCase();

    if (status === "BOOKED") {
      return {
        background: "#dcfce7",
        color: "#166534",
      };
    }

    return {
      background: "#fef3c7",
      color: "#92400e",
    };
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loader}></div>

        <h2 style={styles.loadingText}>Loading Payment Details...</h2>
      </div>
    );
  }

  // =========================================================
  // UI
  // =========================================================

  return (
    <div style={styles.page}>
      {/* TOAST */}

      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "success" ? "#16a34a" : "#dc2626",
          }}
        >
          {toast.message}
        </div>
      )}

      {/* HEADER */}

      <div style={styles.headerCard}>
        <div>
          <h1 style={styles.title}>💳 User Payment Details</h1>

          <p style={styles.subtitle}>Manage tenant and guest payments</p>
        </div>

        <button style={styles.refreshButton} onClick={fetchUsers}>
          🔄 Refresh
        </button>
      </div>

      {/* SUMMARY */}

      <div style={styles.summaryContainer}>
        {/* TOTAL BED AMOUNT */}

        <div style={styles.summaryCard}>
          <div
            style={{
              ...styles.summaryIcon,
              background: "#dbeafe",
            }}
          >
            💰
          </div>

          <div>
            <div style={styles.summaryLabel}>Total Beds Amount</div>

            <div
              style={{
                ...styles.summaryValue,
                color: "#2563eb",
              }}
            >
              ₹{totalBedsAmount.toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        {/* PENDING */}

        <div style={styles.summaryCard}>
          <div
            style={{
              ...styles.summaryIcon,
              background: "#fee2e2",
            }}
          >
            ⚠️
          </div>

          <div>
            <div style={styles.summaryLabel}>Pending Amount</div>

            <div
              style={{
                ...styles.summaryValue,
                color: "#dc2626",
              }}
            >
              ₹{totalPendingAmount.toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        {/* RECEIVED */}

        <div style={styles.summaryCard}>
          <div
            style={{
              ...styles.summaryIcon,
              background: "#dcfce7",
            }}
          >
            ✅
          </div>

          <div>
            <div style={styles.summaryLabel}>Received Amount</div>

            <div
              style={{
                ...styles.summaryValue,
                color: "#16a34a",
              }}
            >
              ₹{totalPaidAmount.toLocaleString("en-IN")}
            </div>
          </div>
        </div>
      </div>

      {/* FILTER */}

      <div style={styles.filterCard}>
        <span style={styles.filterTitle}>Payment Filter</span>

        <button
          style={filter === "ALL" ? styles.activeFilter : styles.filterButton}
          onClick={() => setFilter("ALL")}
        >
          All ({users.length})
        </button>

        <button
          style={
            filter === "PENDING"
              ? styles.pendingActiveFilter
              : styles.filterButton
          }
          onClick={() => setFilter("PENDING")}
        >
          ⏳ Pending ({pendingCount})
        </button>

        <button
          style={
            filter === "PAID" ? styles.paidActiveFilter : styles.filterButton
          }
          onClick={() => setFilter("PAID")}
        >
          ✅ Paid ({paidCount})
        </button>
      </div>

      {/* TABLE */}

      <div style={styles.tableCard}>
        <div style={styles.tableHeader}>
          <h2 style={styles.tableTitle}>Payment List</h2>

          <span style={styles.resultCount}>{filteredUsers.length} Records</span>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>

                <th style={styles.th}>Name</th>

                <th style={styles.th}>Contact No</th>

                <th style={styles.th}>Bed</th>

                <th style={styles.th}>Bed Status</th>

                <th style={styles.th}>Bed Cost</th>

                <th style={styles.th}>Paid Amount</th>

                <th style={styles.th}>Pending Amount</th>

                <th style={styles.th}>Payment Status</th>

                <th style={styles.th}>Last Bed Date</th>

                <th style={styles.th}>Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="12" style={styles.noData}>
                    <div style={styles.noDataIcon}>💳</div>

                    <div>No payment records found.</div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, index) => {
                  const pendingAmount = getPendingAmount(user);

                  const pending = isPendingPayment(user);

                  const paymentStatusStyle = getPaymentStatusStyle(user);

                  const bedStatusStyle = getBedStatusStyle(user);

                  return (
                    <tr key={user.userRegisterId} style={styles.tr}>
                      {/* # */}

                      <td style={styles.td}>{index + 1}</td>

                      {/* NAME */}

                      <td style={styles.td}>
                        <div style={styles.nameContainer}>
                          <div style={styles.smallAvatar}>👤</div>

                          <div>
                            <strong>{user.fullName || "-"}</strong>
                          </div>
                        </div>
                      </td>

                      {/* CONTACT */}

                      <td style={styles.td}>{user.contactNo || "-"}</td>

                      {/* BED */}

                      <td style={styles.td}>
                        <span style={styles.bedBadge}>
                          🛏️ {user.userBedName || "-"}
                        </span>
                      </td>

                      {/* BED STATUS */}

                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            ...bedStatusStyle,
                          }}
                        >
                          {String(user.bedStatus || "").toUpperCase() ===
                          "BOOKED"
                            ? `BOOKED / ${getPaymentType(user)}`
                            : user.bedStatus || "-"}
                        </span>
                      </td>

                      {/* PAYMENT TYPE */}

                      {/* <td style={styles.td}>
                        <span style={styles.paymentTypeBadge}>
                          💰 {getPaymentType(user)}
                        </span>
                      </td> */}

                      {/* BED COST */}

                      <td style={styles.td}>
                        ₹{Number(user.userBedCost || 0).toLocaleString("en-IN")}
                      </td>

                      {/* PAID */}

                      <td style={styles.td}>
                        ₹
                        {Number(user.userPaidAmount || 0).toLocaleString(
                          "en-IN",
                        )}
                      </td>

                      {/* PENDING */}

                      <td style={styles.td}>
                        <strong
                          style={{
                            color: pending ? "#dc2626" : "#16a34a",
                          }}
                        >
                          ₹{pendingAmount.toLocaleString("en-IN")}
                        </strong>
                      </td>

                      {/* PAYMENT STATUS */}

                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.paymentBadge,
                            background: paymentStatusStyle.background,
                            color: paymentStatusStyle.color,
                          }}
                        >
                          {getPaymentStatusLabel(user)}
                        </span>
                      </td>

                      {/* LAST BED DATE */}

                      <td style={styles.td}>
                        {formatBedDate(user.userBedVacateDate)}
                      </td>

                      {/* ACTION */}

                      <td style={styles.actionTd}>
                        {pending ? (
                          <>
                            <button
                              style={styles.payButton}
                              onClick={() => handlePayAmount(user)}
                              disabled={
                                payingUser?.userRegisterId ===
                                user.userRegisterId
                              }
                            >
                              💰 Pay Amount
                            </button>

                            <button
                              style={styles.messageButton}
                              onClick={() => handleSendMessage(user)}
                              disabled={sendingMessage === user.userRegisterId}
                            >
                              {sendingMessage === user.userRegisterId
                                ? "Sending..."
                                : "📩 Send Message"}
                            </button>
                          </>
                        ) : (
                          <span style={styles.paidAction}>✓ Paid</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default UserPaymentDetails;

// =========================================================
// STYLES
// =========================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg,#eff6ff,#f8fafc,#ffffff)",
    padding: "30px",
  },

  headerCard: {
    background: "#ffffff",
    borderRadius: "18px",
    padding: "25px 30px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    boxShadow: "0 8px 25px rgba(0,0,0,.07)",
  },

  title: {
    margin: 0,
    color: "#1e3a8a",
    fontSize: "28px",
    fontWeight: "800",
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#64748b",
  },

  refreshButton: {
    border: "none",
    background: "#2563eb",
    color: "#fff",
    padding: "11px 20px",
    borderRadius: "10px",
    fontWeight: "700",
    cursor: "pointer",
  },

  summaryContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
    gap: "20px",
    marginBottom: "20px",
  },

  summaryCard: {
    background: "#fff",
    borderRadius: "16px",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    gap: "15px",
    boxShadow: "0 8px 25px rgba(0,0,0,.07)",
  },

  summaryIcon: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
  },

  summaryLabel: {
    color: "#64748b",
    fontSize: "14px",
  },

  summaryValue: {
    fontSize: "25px",
    fontWeight: "800",
    marginTop: "3px",
  },

  filterCard: {
    background: "#fff",
    padding: "18px 20px",
    borderRadius: "15px",
    boxShadow: "0 8px 25px rgba(0,0,0,.07)",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "20px",
  },

  filterTitle: {
    fontWeight: "800",
    color: "#334155",
    marginRight: "10px",
  },

  filterButton: {
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#475569",
    padding: "9px 17px",
    borderRadius: "9px",
    fontWeight: "700",
    cursor: "pointer",
  },

  activeFilter: {
    border: "none",
    background: "#2563eb",
    color: "#fff",
    padding: "9px 17px",
    borderRadius: "9px",
    fontWeight: "700",
    cursor: "pointer",
  },

  pendingActiveFilter: {
    border: "none",
    background: "#dc2626",
    color: "#fff",
    padding: "9px 17px",
    borderRadius: "9px",
    fontWeight: "700",
    cursor: "pointer",
  },

  paidActiveFilter: {
    border: "none",
    background: "#16a34a",
    color: "#fff",
    padding: "9px 17px",
    borderRadius: "9px",
    fontWeight: "700",
    cursor: "pointer",
  },

  tableCard: {
    background: "#fff",
    borderRadius: "18px",
    boxShadow: "0 10px 30px rgba(0,0,0,.08)",
    overflow: "hidden",
  },

  tableHeader: {
    padding: "20px 25px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #e2e8f0",
  },

  tableTitle: {
    margin: 0,
    color: "#1e293b",
    fontSize: "20px",
  },

  resultCount: {
    background: "#eff6ff",
    color: "#2563eb",
    padding: "6px 12px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: "700",
  },

  tableWrapper: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1500px",
  },

  th: {
    background: "#f8fafc",
    padding: "15px 12px",
    textAlign: "left",
    color: "#475569",
    fontSize: "13px",
    fontWeight: "800",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },

  tr: {
    borderBottom: "1px solid #f1f5f9",
  },

  td: {
    padding: "15px 12px",
    color: "#334155",
    fontSize: "14px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },

  nameContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  smallAvatar: {
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    background: "#dbeafe",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  bedBadge: {
    background: "#eff6ff",
    color: "#1d4ed8",
    padding: "6px 10px",
    borderRadius: "7px",
    fontWeight: "700",
  },

  statusBadge: {
    padding: "6px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "800",
  },

  paymentTypeBadge: {
    background: "#ede9fe",
    color: "#6d28d9",
    padding: "6px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "800",
  },

  paymentBadge: {
    padding: "6px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "800",
  },

  actionTd: {
    padding: "12px",
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  payButton: {
    border: "none",
    background: "#2563eb",
    color: "#fff",
    padding: "9px 12px",
    borderRadius: "8px",
    fontWeight: "700",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  messageButton: {
    border: "none",
    background: "#16a34a",
    color: "#fff",
    padding: "9px 12px",
    borderRadius: "8px",
    fontWeight: "700",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  paidAction: {
    color: "#16a34a",
    fontWeight: "800",
  },

  noData: {
    textAlign: "center",
    padding: "60px",
    color: "#64748b",
    fontSize: "16px",
  },

  noDataIcon: {
    fontSize: "40px",
    marginBottom: "10px",
  },

  toast: {
    position: "fixed",
    top: "25px",
    right: "25px",
    color: "#fff",
    padding: "14px 22px",
    borderRadius: "10px",
    fontWeight: "700",
    boxShadow: "0 8px 25px rgba(0,0,0,.25)",
    zIndex: 9999,
  },

  loadingContainer: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    background: "#eff6ff",
  },

  loadingText: {
    color: "#2563eb",
  },

  loader: {
    width: "60px",
    height: "60px",
    border: "6px solid #dbeafe",
    borderTop: "6px solid #2563eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "15px",
  },
};
